import { useEffect, useRef, useState, type ReactNode } from "react";

export type ComboOption = { v: string; n: string; d?: string };

function highlight(text: string, q: string): ReactNode {
  if (!q) return text;
  const i = text.toLowerCase().indexOf(q.toLowerCase());
  if (i < 0) return text;
  return (
    <>
      {text.slice(0, i)}
      <mark>{text.slice(i, i + q.length)}</mark>
      {text.slice(i + q.length)}
    </>
  );
}

export function Combobox({
  id,
  value,
  options,
  placeholder = "Search…",
  ariaLabel,
  disabled,
  allowFreeText,
  onPick,
  onQuery,
}: {
  id: string;
  value: string;
  options: ComboOption[];
  placeholder?: string;
  ariaLabel?: string | undefined;
  disabled?: boolean | undefined;
  allowFreeText?: boolean | undefined;
  onPick(v: string): void;
  onQuery?: ((text: string) => void) | undefined;
}) {
  // The input shows an option's name; `value` is only what is committed (BUG-009).
  // An empty value leaves the box blank so the placeholder shows.
  const labelOf = (v: string) => (v === "" ? "" : options.find((o) => o.v === v)?.n || v);
  const display = labelOf(value);
  const [text, setText] = useState(display);
  const [open, setOpen] = useState(false);
  const [cursor, setCursor] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => setText(display), [display]);

  const q = open && text !== display ? text : "";
  const filtered = options.filter((o) =>
    `${o.v} ${o.n} ${o.d ?? ""}`.toLowerCase().includes(q.toLowerCase()),
  );
  const at = cursor >= filtered.length ? 0 : cursor;

  useEffect(() => {
    const el = listRef.current?.querySelector(".is-cursor");
    el?.scrollIntoView?.({ block: "nearest" });
  }, [at, open]);

  const commit = (v: string) => {
    setText(labelOf(v));
    setOpen(false);
    onQuery?.("");
    onPick(v);
  };
  const revert = () => {
    setText(display);
    setOpen(false);
    onQuery?.("");
  };

  return (
    <div className={`combo${open ? " is-open" : ""}`} id={id}>
      <input
        className="input"
        type="text"
        role="combobox"
        aria-expanded={open}
        aria-controls={`${id}-list`}
        aria-autocomplete="list"
        aria-label={ariaLabel}
        disabled={disabled}
        aria-activedescendant={open && filtered[at] ? `${id}-o${at}` : undefined}
        autoComplete="off"
        spellCheck={false}
        value={text}
        placeholder={placeholder}
        onFocus={(e) => {
          setCursor(0);
          setOpen(true);
          e.currentTarget.select();
        }}
        onChange={(e) => {
          setCursor(0);
          setText(e.target.value);
          setOpen(true);
          onQuery?.(e.target.value);
        }}
        onKeyDown={(e) => {
          if (e.key === "ArrowDown") {
            setCursor(Math.min(at + 1, filtered.length - 1));
            e.preventDefault();
          } else if (e.key === "ArrowUp") {
            setCursor(Math.max(at - 1, 0));
            e.preventDefault();
          } else if (e.key === "Enter") {
            if (filtered[at]) commit(filtered[at].v);
            else if (allowFreeText && text !== display) commit(text);
            e.preventDefault();
          } else if (e.key === "Escape") {
            revert();
          }
        }}
        onBlur={() => {
          // Only an exact id commits on blur: two options can share a name.
          const exact = options.find((o) => o.v === text);
          if (exact && exact.v !== value) commit(exact.v);
          // A catalogue that has not been discovered yet must not block a hand-typed source.
          else if (allowFreeText && text.trim() && text !== display) commit(text.trim());
          else revert();
        }}
      />
      {open ? (
        <div className="combo-list" id={`${id}-list`} ref={listRef} role="listbox">
          {filtered.length === 0 ? (
            <div className="combo-empty">
              No field matches “{q}”{allowFreeText ? " — press Enter to use it as typed" : ""}
            </div>
          ) : (
            filtered.map((o, i) => (
              <div
                className={`combo-opt${i === at ? " is-cursor" : ""}`}
                id={`${id}-o${i}`}
                key={o.v}
                role="option"
                aria-selected={i === at}
                onMouseDown={(e) => {
                  e.preventDefault();
                  commit(o.v);
                }}
              >
                <div className="o1">{highlight(o.n || o.v, q)}</div>
                {o.d ? <div className="o2">{o.d}</div> : null}
              </div>
            ))
          )}
        </div>
      ) : null}
    </div>
  );
}
